import _ from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from 'user-auth'
import { getUserLink, uploadFile } from 'utils'
import { getUserDefaultWorkspace } from '~workspace/api/getUserDefaultWorkspace'
import getWorkspaceLogoUploadUrl from '~workspace/api/getWorkspaceLogoUploadUrl'
import { useSearchMapsByWorkspaceIdQuery } from '~workspace/hooks/useSearchMapsByWorkspaceIdQuery'
import {
  useDeleteWorkspaceByIdMutation,
  useUpdateWorkspaceByIdMutation
} from '~workspace/services/workspace'
import { useWorkspace } from '~workspace/states/workspace'
import { View } from './view'

const Settings = () => {
  const router = useRouter()

  const { user } = useAuth()
  const [warningMessage, setWarningMessage] = useState(null)
  const [workspaceState, setWorkspaceState] = useState({})
  const workspace = useWorkspace()
  const { total: mapsCount } = useSearchMapsByWorkspaceIdQuery()

  const [updateWorkspaceById, { isLoading: updateWorkspaceLoading }] =
    useUpdateWorkspaceByIdMutation()
  const [deleteWorkspaceById, { isLoading: deleteWorkspaceLoading }] =
    useDeleteWorkspaceByIdMutation()

  const disableSave =
    _.isEmpty(workspaceState.name) ||
    !isEmailValid(workspaceState.email) ||
    !isUrlValid(workspaceState.websiteUrl) ||
    !isUrlValid(workspaceState.communityUrl)

  useEffect(() => {
    setWorkspaceState(workspace)
  }, [workspace])

  const validation = useMemo(
    () => ({
      email: isEmailValid(workspaceState.email),
      websiteUrl: isUrlValid(workspaceState.websiteUrl),
      communityUrl: isUrlValid(workspaceState.communityUrl)
    }),
    [
      workspaceState.email,
      workspaceState.websiteUrl,
      workspaceState.communityUrl
    ]
  )

  const handleUpdateWorkspace = async () => {
    const {
      id,
      name,
      description,
      email,
      websiteUrl,
      communityUrl,
      twitterUsername
    } = workspaceState

    await updateWorkspaceById({
      id,
      name,
      description: description || '',
      email: email || '',
      websiteUrl: websiteUrl || '',
      communityUrl: communityUrl || '',
      twitterUsername: twitterUsername || ''
    })

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleChange = (key, value) => {
    setWorkspaceState((prevState) => ({ ...prevState, [key]: value }))
  }

  const handleImageChange = async (imageFile) => {
    const { uploadUrl, formFieldsJson, path } = await getWorkspaceLogoUploadUrl(
      { id: workspaceState.id }
    )

    await uploadFile({
      url: uploadUrl,
      formData: JSON.parse(formFieldsJson),
      file: imageFile
    })

    const { data: updatedWorkspace } = await updateWorkspaceById({
      id: workspace.id,
      logo: path
    })

    setWorkspaceState((prevState) => ({
      ...prevState,
      logo: updatedWorkspace.logo
    }))

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async () => {
    const { defaultWorkspaceId } = await getUserDefaultWorkspace(user?.id)

    if (workspaceState.id === defaultWorkspaceId) {
      setWarningMessage(WARNING_MESSAGES.DEFAULT_WORKSPACE)
      return
    }

    const deletable = mapsCount === 0

    if (!deletable) {
      setWarningMessage(WARNING_MESSAGES.MAPS)
      return
    }

    await deleteWorkspaceById(workspaceState.id)

    router.push(
      getUserLink({
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })
    )
  }

  return (
    <View
      updateWorkspaceLoading={updateWorkspaceLoading}
      deleteWorkspaceLoading={deleteWorkspaceLoading}
      workspace={workspaceState}
      onChange={handleChange}
      onImageChange={handleImageChange}
      onDelete={handleDelete}
      onUpdateWorkspace={handleUpdateWorkspace}
      disableSave={disableSave}
      warningMessage={warningMessage}
      validation={validation}
    />
  )
}

export { Settings }
export default Settings

const isEmailValid = (str) =>
  !str
    ? true
    : String(str).match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )

const isUrlValid = (str) =>
  !str
    ? true
    : String(str).match(
        /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/
      )

const WARNING_MESSAGES = {
  DEFAULT_WORKSPACE: 'This is your default workspace, it cannot be deleted.',
  MAPS: 'You cannot delete a workspace that has maps. Please delete all maps first.'
}
